import {useState, useEffect, useRef, useContext} from 'react';
import ReactDOM from 'react-dom';
import {useReducedMotion, motion} from 'framer-motion';
import {FormControllerVariants} from '../../utilities/framerVariants';
import SubmitController from './components/SubmitController/SubmitController';
import Form from './components/Form/Form';
import {AppContext} from "../../context/AppContext";
import styles from './FormController.module.scss'
import Icon from "../elements/Icon/Icon";
import {Link} from "react-router-dom";
import {useSelector} from "react-redux";

const FormController = () => {
    const {profile} = useSelector((state) => state.profile);
    const {windowWidth, isFormOpen, isInvoiceEdited, setIsFormOpen, setIsInvoiceEdited} = useContext(AppContext);
    const isTablet = windowWidth >= 768;
    const [isFormEdited] = useState(isFormOpen && isInvoiceEdited);
    const [formState, setFormState] = useState({
        date: new Date(),
        number: "",
        customer: {name: "", email: "", address: {street: "", district: "", city: "", postCode: "", region: ""}},
        sender: {
            name: "",
            email: "",
            address: {street: "", city: "", postCode: "", country: ""},
            address_ukr: {street_ukr: "", city_ukr: "", postCode_ukr: "", country_ukr: ""}
        },
        billingInfo: {bicCode: "", iban: "", beneficiaryBank: "", bankAddress: "", beneficiary: ""},
        dueDate: "",
        total: 0,
        items: [],
    });
    const formRef = useRef();
    const backdropRef = useRef();
    const hasScroll = windowWidth > document.documentElement.clientWidth;
    const shouldReduceMotion = useReducedMotion();

    const variant = (element) => {
        return shouldReduceMotion
            ? FormControllerVariants.reduced
            : FormControllerVariants[element];
    };

    useEffect(() => {
        document.addEventListener('keydown', focusTrap);
        document.addEventListener('click', handleClickOutsideForm);
        formRef.current.focus();
        document.body.style.overflow = 'hidden';
        hasScroll && (document.body.style.paddingRight = '17px');

        return () => {
            document.removeEventListener('keydown', focusTrap);
            document.removeEventListener('click', handleClickOutsideForm);
            document.body.style.overflow = 'unset';
            document.body.style.paddingRight = 'unset';
        };
    }, []);

    const handleClickOutsideForm = (event) => {
        const target = event.target;
        if (target === backdropRef.current) handleDiscard();
    };

    const focusTrap = (event) => {
        if (event.key === 'Escape') handleDiscard();
        if (event.key !== 'Tab') return;

        const formElements =
            formRef.current.querySelectorAll('button, a, input');
        const firstElement = formElements[0];
        const lastElement = formElements[formElements.length - 1];

        if (!event.shiftKey && document.activeElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
        }

        if (event.shiftKey && document.activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
        }
    };

    const handleDiscard = () => {
        setIsFormOpen(false);
        setIsInvoiceEdited(false);
    }

    useEffect(() => {
        if (profile) {
            setFormState((prevState) => ({
                ...prevState,
                sender: {
                    name: profile.name || "",
                    email: profile.email || "",
                    address: {
                        street: profile.address?.street || "",
                        city: profile.address?.city || "",
                        postCode: profile.address?.postCode || "",
                        country: profile.address?.country || "",
                    },
                    address_ukr: {
                        street_ukr: profile.address_ukr?.street_ukr || "",
                        city_ukr: profile.address_ukr?.city_ukr || "",
                        postCode_ukr: profile.address_ukr?.postCode_ukr || "",
                        country_ukr: profile.address_ukr?.country_ukr || "",
                    },
                },
                billingInfo: {
                    ...prevState.billingInfo,
                    beneficiary: `${profile.name || ""} ${profile.surname || ""}`.trim(),
                },
            }));
        }
        console.log(profile);
    }, [profile]);

    const controller = (
        <>
            <motion.div
                className={styles.backdrop}
                ref={backdropRef}
                variants={variant('backdrop')}
                initial="hidden"
                animate="visible"
                exit="exit"
            />
            <motion.div
                className={styles.formController}
                aria-modal
                aria-label="Invoice form"
                tabIndex={-1}
                role="dialog"
                ref={formRef}
                variants={variant('form')}
                initial="hidden"
                animate="visible"
                exit="exit"
            >
                {!isTablet && (
                    <Link className={styles.link} to="/" onClick={handleDiscard}>
                        <Icon
                            name={'arrow-left'}
                            size={10}
                            color={'var(--color-teal)'}
                        />
                        Go back
                    </Link>
                )}
                <Form isEdited={isFormEdited} formState={formState} setFormState={setFormState}/>
                <SubmitController formState={formState} setFormState={setFormState}/>
            </motion.div>
        </>
    );

    return ReactDOM.createPortal(controller, document.body);
};

export default FormController;
